import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Phone, Mail, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const { t, language } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    organization: '',
    country: '',
    message: '',
  });

  // Coordonnées professionnelles
  const contactInfo = {
    email: 'contacts@lenoda-services.com',
    phones: [
      { country: '🇫🇷', number: '+33 6 59 34 56 40' },
      { country: '🇫🇷', number: '+33 6 12 84 56 45' },
      { country: '🇫🇷', number: '+33 6 14 65 22 51' },
      { country: '🇧🇷', number: '+55 85 99 71 08 26' },
      { country: '🇨🇲', number: '+237 6 77 63 25 82' },
      { country: '🇬🇼', number: '+245 955 566 174' },
    ],
    addresses: {
      fr: { city: 'Roquebrune Cap Martin', address: '583 Chemin du Vallonet 06190 Roquebrune Cap Martin' },
      cm: { city: 'Yaoundé', address: 'Derrière COMBATANT BP 6819 YAOUNDE' },
      gw: { city: 'Bissau', address: 'Rua de Clinica de Bor/Bor' }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, { ...formData, language });
      toast.success(t('contact.success'));
      setFormData({
        full_name: '',
        email: '',
        organization: '',
        country: '',
        message: '',
      });
    } catch (error) {
      toast.error(t('contact.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-32">
      <section className="py-16 md:py-24" data-testid="contact-hero">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-sky-100 rounded-full mb-4">
              <p className="text-sm font-semibold text-sky-600 uppercase tracking-wider">
                {language === 'fr' ? 'Cabinet d\'Ingénierie en Souveraineté' : 
                 language === 'en' ? 'Sovereignty Engineering Firm' :
                 language === 'pt' ? 'Gabinete de Engenharia em Soberania' :
                 language === 'it' ? 'Studio di Ingegneria della Sovranità' :
                 language === 'ru' ? 'Кабинет инженерии суверенитета' :
                 '主权工程咨询公司'}
              </p>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
              {t('contact.title')}
            </h1>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Formulaire */}
            <div className="bg-slate-50 border border-slate-200 rounded-3xl p-8">
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.fullName')} *
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      data-testid="contact-fullname-input"
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      data-testid="contact-email-input"
                      required
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.organization')}
                    </label>
                    <input
                      type="text"
                      name="organization"
                      value={formData.organization}
                      onChange={handleChange}
                      data-testid="contact-organization-input"
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contact.country')}
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      data-testid="contact-country-input"
                      className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contact.message')} *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    data-testid="contact-message-input"
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="contact-submit-button"
                  className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-sky-600 text-white rounded-xl font-medium hover:bg-sky-700 transition-colors disabled:opacity-50"
                >
                  {isLoading ? (
                    <>
                      {language === 'fr' && 'Envoi en cours...'}
                      {language === 'en' && 'Sending...'}
                      {language === 'pt' && 'Enviando...'}
                      {language === 'it' && 'Invio in corso...'}
                      {language === 'ru' && 'Отправка...'}
                      {language === 'zh' && '发送中...'}
                    </>
                  ) : (
                    <>
                      {t('contact.submit')} <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Coordonnées */}
            <div className="space-y-6">
              {/* Email */}
              <div className="bg-sky-50 border border-sky-200 rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-sky-600 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-700 uppercase">
                      {language === 'fr' ? 'Email Professionnel' : 
                       language === 'en' ? 'Professional Email' : 
                       language === 'pt' ? 'Email Profissional' : 
                       language === 'it' ? 'Email Professionale' :
                       language === 'ru' ? 'Профессиональная почта' :
                       '专业邮箱'}
                    </p>
                    <a href={`mailto:${contactInfo.email}`} className="text-lg font-bold text-sky-600 hover:text-sky-700 transition-colors">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </div>

              {/* Téléphones */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-slate-700 uppercase">
                    {language === 'fr' ? 'Lignes Directes' : 
                     language === 'en' ? 'Direct Lines' : 
                     language === 'pt' ? 'Linhas Diretas' : 
                     language === 'it' ? 'Linee Dirette' :
                     language === 'ru' ? 'Прямые линии' :
                     '直接电话'}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {contactInfo.phones.map((phone, i) => (
                    <a 
                      key={i}
                      href={`tel:${phone.number.replace(/\s/g, '')}`}
                      className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-600 transition-colors"
                    >
                      <span>{phone.country}</span>
                      <span className="font-medium">{phone.number}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* Sièges */}
              <div className="bg-slate-100 border border-slate-200 rounded-2xl p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-slate-700 uppercase">
                    {language === 'fr' ? 'Sièges Sociaux' : 
                     language === 'en' ? 'Headquarters' : 
                     language === 'pt' ? 'Sedes' : 
                     language === 'it' ? 'Sedi' :
                     language === 'ru' ? 'Штаб-квартиры' :
                     '总部'}
                  </p>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">🇫🇷 France</p>
                    <p className="text-xs text-slate-600">{contactInfo.addresses.fr.address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">🇨🇲 Cameroun</p>
                    <p className="text-xs text-slate-600">{contactInfo.addresses.cm.address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">🇬🇼 Guinée-Bissau</p>
                    <p className="text-xs text-slate-600">{contactInfo.addresses.gw.address}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
